import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";

interface PopularItem {
  name: string;
  count: number;
  revenue: number;
}

interface TopSellingItemsChartProps {
  popularItems: PopularItem[];
  height?: number;
  maxItems?: number;
  loading?: boolean;
}

const COLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#84cc16", "#f97316", "#ec4899", "#6366f1"];

const TopSellingItemsChart: React.FC<TopSellingItemsChartProps> = ({ popularItems, height = 400, maxItems = 8, loading = false }) => {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const FormatAmount = useFormatAmount()

  const chartData = useMemo(
    () =>
      popularItems.slice(0, maxItems).map((item, i) => ({
        ...item,
        displayName: item.name.length > (isMobile ? 12 : 20) ? `${item.name.slice(0, isMobile ? 12 : 20)}...` : item.name,
        color: COLORS[i % COLORS.length],
      })),
    [popularItems, maxItems, isMobile]
  );

  const totals = useMemo(() => {
    const totalRevenue = chartData.reduce((s, i) => s + i.revenue, 0);
    const totalQty = chartData.reduce((s, i) => s + i.count, 0);
    return {
      totalRevenue,
      totalQty,
      avg: totalQty ? totalRevenue / totalQty : 0,
    };
  }, [chartData]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 animate-pulse space-y-2">
        <div className="h-6 bg-gray-200 rounded w-1/3" />
        <div className="h-64 bg-gray-200 rounded" />
      </div>
    );
  }

  if (!popularItems.length) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-2 text-center">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Top Selling Items</h2>
        <hr className="mb-6 border-gray-200" />
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <div className="text-6xl mb-3">📊</div>
          <p>No Sales Data</p>
          <p className="text-sm text-gray-500">Sales data will appear when items start selling.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Top Selling Items</h2>
          <p className="text-sm text-gray-500 mt-1">{chartData.length} items by revenue</p>
        </div>
      </div>

      <hr className="mb-6 border-gray-200" />

      {/* Chart */}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{
              top: 10,
              right: isMobile ? 10 : 30,
              left: isMobile ? 80 : 100,
              bottom: 10,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis type="number" tick={{ fill: "#6b7280", fontSize: 12 }} tickFormatter={(v) => `$${v.toLocaleString()}`} />
            <YAxis type="category" dataKey="displayName" tick={{ fill: "#6b7280", fontSize: 12 }} width={isMobile ? 80 : 100} />
            <Tooltip
              cursor={{ fill: "#f9fafb" }}
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div className="bg-white p-3 rounded-lg shadow border border-gray-200 text-sm">
                    <p className="font-semibold text-gray-900 mb-2">{payload[0].payload.name}</p>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>Revenue:</span>
                        <span className="text-green-600 font-medium">{FormatAmount(payload[0].payload.revenue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Quantity:</span>
                        <span className="text-blue-600 font-medium">{payload[0].payload.count}</span>
                      </div>
                    </div>
                  </div>
                ) : null
              }
            />
            <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary */}
      <hr className="my-3 border-gray-200" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center text-sm">
        <div>
          <p className="text-gray-500 mb-1">Total Revenue</p>
          <p className="font-bold text-green-600">{FormatAmount(totals.totalRevenue)}</p>
        </div>
        <div>
          <p className="text-gray-500 mb-1">Total Quantity</p>
          <p className="font-bold text-blue-600">{totals.totalQty.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500 mb-1">Avg per Item</p>
          <p className="font-bold text-purple-600">{FormatAmount(totals.avg)}</p>
        </div>
      </div>
    </div>
  );
};

export default TopSellingItemsChart;